import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supaFetch } from '../lib/supabase.js';
import { queryKeys } from '../lib/queryKeys.js';
import { VENDOR_FN } from '../lib/constants.js';
import { getValidToken } from '../lib/supabase.js';
import { SUPABASE_KEY } from '../lib/constants.js';

// ─── ADMIN: all vendors ───────────────────────────────────────────────────────
export function useVendors() {
  return useQuery({
    queryKey:  queryKeys.admin.vendors(),
    queryFn:   () => supaFetch('GET', 'vendors', null, '?order=created_at.desc'),
    select:    (result) => result.data ?? [],
    staleTime: 5 * 60 * 1000,
    gcTime:    10 * 60 * 1000,
  });
}

// ─── ADMIN: vendor products awaiting review ───────────────────────────────────
export function usePendingVendorProducts() {
  return useQuery({
    queryKey:  queryKeys.admin.vendorPendingProducts(),
    queryFn:   () => supaFetch('GET', 'products', null,
      '?approval_status=eq.pending&vendor_id=not.is.null&select=id,name_en,name_ar,sku,category,price,min_qty,stock,image,vendor_id,created_at&order=created_at.desc'),
    select:    (result) => result.data ?? [],
    staleTime: 60 * 1000,
  });
}

// ─── ADMIN: approve / reject a vendor product ─────────────────────────────────
export function useReviewVendorProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }) =>
      supaFetch('PATCH', 'products', { approval_status: decision }, `?id=eq.${encodeURIComponent(id)}`),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.vendorPendingProducts() });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
    },
  });
}

// ─── ADMIN: vendor lifecycle via manage-vendor edge function ──────────────────
// action: 'approve' (+ email/password) | 'suspend' | 'reactivate' | 'reject' | 'reset_password'
export function useManageVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const token = await getValidToken();
      if (!token) throw new Error('Session expired — sign in again');
      const res = await fetch(VENDOR_FN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Request failed');
      return data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.vendorsRoot() });
    },
  });
}

// ─── VENDOR PORTAL: own products ──────────────────────────────────────────────
export function useMyVendorProducts(vendorId) {
  return useQuery({
    queryKey:  queryKeys.vendor.myProducts(vendorId),
    enabled:   !!vendorId,
    queryFn:   () => supaFetch('GET', 'products', null,
      `?vendor_id=eq.${encodeURIComponent(vendorId)}&select=id,name_en,name_ar,sku,category,category_ar,price,min_qty,carton_qty,stock,image,images,description,description_ar,approval_status,is_active,created_at&order=created_at.desc`),
    select:    (result) => result.data ?? [],
    staleTime: 60 * 1000,
  });
}

// ─── VENDOR PORTAL: create / update own product (always lands pending) ────────
export function useSaveVendorProduct(vendorId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (product) => {
      const row = { ...product, vendor_id: vendorId, approval_status: 'pending' };
      if (row._isNew) {
        delete row._isNew;
        await supaFetch('POST', 'products', row);
      } else {
        const { id, ...rest } = row;
        await supaFetch('PATCH', 'products', rest, `?id=eq.${encodeURIComponent(id)}`);
      }
      return product;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vendor.myProductsRoot() });
    },
  });
}

// ─── VENDOR PORTAL: batch create/update own products (all land pending) ───────
// Takes rows already split into new (insert) and update (patch by id) buckets.
// vendor_id + approval_status are forced HERE, never trusted from the caller,
// so a batch can't be tricked into writing another vendor's row or skipping
// review. New rows go in one bulk POST; updates are patched per id.
export function useBulkSaveVendorProducts(vendorId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ newRows = [], updateRows = [] }) => {
      const stamp = (r) => ({ ...r, vendor_id: vendorId, approval_status: 'pending', is_active: true });
      if (newRows.length) {
        await supaFetch('POST', 'products', newRows.map(stamp));
      }
      for (const r of updateRows) {
        const { id, ...rest } = stamp(r);
        await supaFetch('PATCH', 'products', rest, `?id=eq.${encodeURIComponent(id)}&vendor_id=eq.${encodeURIComponent(vendorId)}`);
      }
      return { added: newRows.length, updated: updateRows.length };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vendor.myProductsRoot() });
    },
  });
}

// ─── VENDOR PORTAL: bulk patch own products (e.g. show/hide) ──────────────────
// Every PATCH is scoped to the vendor's own rows (id AND vendor_id), so a
// crafted id can't touch someone else's product even if RLS were misconfigured.
export function useBulkUpdateVendorProducts(vendorId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, patch }) => {
      for (const id of ids) {
        await supaFetch('PATCH', 'products', patch, `?id=eq.${encodeURIComponent(id)}&vendor_id=eq.${encodeURIComponent(vendorId)}`);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vendor.myProductsRoot() });
    },
  });
}

// ─── VENDOR PORTAL: hard-delete own products (undo a batch import) ─────────────
// Same vendor-scoped filter. Used to reverse a just-imported batch; only the
// newly-inserted ids are passed, mirroring the admin importer's revert.
export function useDeleteVendorProducts(vendorId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await supaFetch('DELETE', 'products', null, `?id=eq.${encodeURIComponent(id)}&vendor_id=eq.${encodeURIComponent(vendorId)}`);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vendor.myProductsRoot() });
    },
  });
}
